import { useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { useImpersonationSnapshot } from '@opendoor/auth-fe/impersonation-state';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { impersonationSession } from 'components/account/impersonation';
import { useTimeBoundedFeatureFlagEvaluator } from 'components/helpers/hooks';

/**
 * Kill switch for the in-browser tour booking flow. Off or unresolved keeps the
 * app-handoff modal that predates it.
 */
export const WEB_TOUR_BOOKING_FLAG = 'buyer-web-tour-booking';
const WEB_TOUR_BOOKING_JOURNEY_FLAG = 'buyer-web-tour-booking-journey-v1';

const FLAG_READ_STALE_MS = 5 * 60 * 1000;

export type WebTourBookingGate = { isResolved: false } | { isResolved: true; isEnabled: boolean };

function useTourFlagGate(flag: string): WebTourBookingGate {
  const { authentication } = useAuth();
  const authenticatedCustomerUuid =
    authentication.state === 'authenticated' ? (authentication.user.customerUuid ?? '') : '';
  const resolvedCustomerUuid =
    authentication.state === 'authenticating' ? undefined : authenticatedCustomerUuid;
  const [settledCustomerUuid, setSettledCustomerUuid] = useState(resolvedCustomerUuid);
  if (resolvedCustomerUuid !== undefined && settledCustomerUuid !== resolvedCustomerUuid) {
    setSettledCustomerUuid(resolvedCustomerUuid);
  }
  // Session refresh must keep the settled cache key to preserve an open booking sheet.
  const customerUuid = resolvedCustomerUuid ?? settledCustomerUuid ?? '';
  const evaluateFlag = useTimeBoundedFeatureFlagEvaluator();
  const query = useQuery(
    queryOptions({
      queryKey: ['tours', 'flag', flag, customerUuid] as const,
      queryFn: () => evaluateFlag(flag),
      // Initial login replay must wait for a settled session key.
      enabled: resolvedCustomerUuid !== undefined,
      retry: false,
      refetchOnWindowFocus: false,
      // The evaluator's own deadline resolves flag-off on a hung BFF, so a resolved false can
      // be a transient failure rather than a real read. A finite staleTime lets that
      // resolution self-heal on a later visit instead of sticking for the whole session; a
      // thrown read is never cached at all and retries on the next mount.
      staleTime: FLAG_READ_STALE_MS,
    }),
  );
  return query.data === undefined
    ? { isResolved: false }
    : { isResolved: true, isEnabled: query.data };
}

export function useWebTourBookingGate(): WebTourBookingGate {
  return useTourFlagGate(WEB_TOUR_BOOKING_FLAG);
}

/** Enable only after Journey accepts the additive booking schema. */
export function useWebTourBookingJourneyGate(): WebTourBookingGate {
  const gate = useTourFlagGate(WEB_TOUR_BOOKING_JOURNEY_FLAG);
  const { customerUUID } = useImpersonationSnapshot(impersonationSession);
  return customerUUID ? { isResolved: true, isEnabled: false } : gate;
}
